import { create } from 'zustand';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

type Tone = 'success' | 'error' | 'info';
interface ToastItem { id: number; tone: Tone; message: string }

interface ToastState {
  items: ToastItem[];
  push: (tone: Tone, message: string) => void;
  dismiss: (id: number) => void;
}

const MAX_TOASTS = 4;
const TIMEOUT_MS = 4000;

let _id = 0;
const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (tone, message) => {
    const id = ++_id;
    set((s) => {
      const next = [...s.items, { id, tone, message }];
      // limita stack
      return { items: next.slice(-MAX_TOASTS) };
    });
    setTimeout(() => set((s) => ({ items: s.items.filter((t) => t.id !== id) })), TIMEOUT_MS);
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

export function useToast() {
  return useToastStore((s) => s.push);
}

const tones: Record<Tone, { icon: typeof CheckCircle2; color: string; bar: string }> = {
  success: { icon: CheckCircle2, color: 'text-moss-300', bar: 'bg-moss-300' },
  error: { icon: AlertCircle, color: 'text-petal-300', bar: 'bg-petal-500' },
  info: { icon: Info, color: 'text-sage-300', bar: 'bg-sage-500' },
};

export function ToastViewport() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className="fixed bottom-s7 right-s7 z-[200] flex flex-col gap-s3 pointer-events-none"
    >
      {items.map((t) => {
        const { icon: Icon, color, bar } = tones[t.tone];
        return (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto group relative flex items-center gap-s3 pl-s5 pr-s3 py-s4 bg-elev border border-[var(--border-strong)] rounded-md min-w-[280px] max-w-[420px] overflow-hidden',
              'animate-toastIn hover:scale-[1.015] transition-transform duration-fast ease-soft',
            )}
            style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.65), 0 8px 16px rgba(0,0,0,0.5)' }}
          >
            {/* progresso 4s */}
            <span
              className={cn('absolute left-0 right-0 bottom-0 h-[2px] origin-left', bar)}
              style={{ animation: `toastProgress ${TIMEOUT_MS}ms linear forwards` }}
            />
            <Icon size={18} strokeWidth={1.5} className={cn('shrink-0', color)} />
            <span className="font-body text-[13px] text-fg-1 flex-1">{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-fast text-fg-3 hover:text-fg-1 p-[2px] rounded-sm"
              aria-label="dispensar"
            >
              <X size={14} strokeWidth={1.6} />
            </button>
          </div>
        );
      })}
      <style>{`
        @keyframes toastProgress {
          from { transform: scaleX(1); opacity: 0.6; }
          to   { transform: scaleX(0); opacity: 0.2; }
        }
      `}</style>
    </div>
  );
}

export default ToastViewport;
